import { UnhandledInReportingError } from '../../../error'
import { DateTimeQuestionNode } from '../dateTimeQuestionNode/dateTimeQuestionNode'
import { TimeMeasurementAnswer } from './timeMeasurementAnswer'
import type { TimeMeasurementQuestion } from './timeMeasurementQuestion'
import type { Node, NodeDict } from '../../node'
import type { QuestionNodeBase, QuestionNodeInterface } from '../questionNode'

export type TimeMeasurementQuestionNode =
  QuestionNodeBase<'timeMeasurement'> & {
    question: TimeMeasurementQuestion
    answer: TimeMeasurementAnswer | undefined
  }

const isTimeMeasurementQuestionNode = (
  node: Node | undefined,
): node is TimeMeasurementQuestionNode => {
  return node?.type === 'question' && node.questionType === 'timeMeasurement'
}
type RequireTimeMeasurementQuestionNode = (
  node: Node | undefined,
) => asserts node is TimeMeasurementQuestionNode
const requireTimeMeasurementQuestionNode: RequireTimeMeasurementQuestionNode =
  node => {
    if (!isTimeMeasurementQuestionNode(node)) {
      throw new UnhandledInReportingError(
        `node is not time measurement question node.`,
        { node },
      )
    }
  }

const isInvalidAnswer = (node: TimeMeasurementQuestionNode): boolean => {
  return node.answer?.isInvalid ?? false
}

/**
 * 対象の日時質問を計測に使っているかを判定する
 */
const isReferencing = (
  node: TimeMeasurementQuestionNode,
  dateTimeQuestionNodeId: number,
): boolean => {
  return (
    node.question.startQuestionNodeId === dateTimeQuestionNodeId ||
    node.question.endQuestionNodeId === dateTimeQuestionNodeId
  )
}

/**
 * 計測に使う開始・終了の日時質問ノードを取得する
 */
const getReferencedNodes = (
  node: TimeMeasurementQuestionNode,
  nodeDict: NodeDict,
): {
  startNode: DateTimeQuestionNode | undefined
  endNode: DateTimeQuestionNode | undefined
} => {
  const startNode = nodeDict[node.question.startQuestionNodeId]
  const endNode = nodeDict[node.question.endQuestionNodeId]
  return {
    startNode: DateTimeQuestionNode.isDateTimeQuestionNode(startNode)
      ? startNode
      : undefined,
    endNode: DateTimeQuestionNode.isDateTimeQuestionNode(endNode)
      ? endNode
      : undefined,
  }
}

const toUserFriendlyAnswerString = (
  node: TimeMeasurementQuestionNode,
): string => {
  return TimeMeasurementAnswer.toDisplayStringAnswer(node.answer)
}

const _TimeMeasurementQuestionNode = {
  isInvalidAnswer,
  isTimeMeasurementQuestionNode,
  isReferencing,
  getReferencedNodes,
  toUserFriendlyAnswerString,
} satisfies QuestionNodeInterface<TimeMeasurementQuestionNode>

export const TimeMeasurementQuestionNode: typeof _TimeMeasurementQuestionNode & {
  requireTimeMeasurementQuestionNode: RequireTimeMeasurementQuestionNode
} = {
  ..._TimeMeasurementQuestionNode,
  requireTimeMeasurementQuestionNode,
}
